// Бот v3: один и тот же ИИ для хотсита (в браузере) и онлайна (на сервере в goja). Все решения детерминированы.
//
// Два источника решений:
// 1. «Примерки»: розыгрыш карты, активацию и ответ на запрос бот пробует на копии состояния — движок сам исполняет
//    эффект — и сравнивает позиции оценочной функцией evaluate (очки, потенциал колоды, жизни, чипсины, ресурсы хода).
//    Копия честная: перед примеркой скрытая информация пересдаётся (чужие руки, порядок колод, стопка жетонов) —
//    бот не знает, есть ли у жертвы защита, и что лежит сверху колоды; атаки примеряются на нескольких пересдачах.
// 2. Статическая модель (synergy.ts) — для покупок и выбора карт на уничтожение/сброс: ценность карты в контексте
//    колоды (мощь, добор, урон, защита, постоянки, синергии) и очки. Покупка оценивается для себя и для соперников:
//    карту, которая сильно усилит лидера, бот забирает, даже если самому она нужна меньше (отъём).
//
// Единицы: evaluate считает в «очках-эквивалентах» (1 = 1 ПО). Мощь и её эквиваленты (P) переводятся в ПО через
// множитель potFactor(T): сколько ПО принесёт 1 P за ход при T оставшихся ходах.
import type { Action, BotLevel, BotStyle, CardDef, CardInst, ChooseCardsPrompt, ChoosePlayerPrompt, Game, Iid, Player, PlayerId, Prompt, PromptAnswer, TraitKey } from '../types.ts';
import { deepClone, card, player, setupOf, setupDone, turnOf, controlled, traitDef, ddkDef } from './state.ts';
import { applyAction, scorePlayer, canActivate, priceFor, chipsPayable, promptWho, restoreRuntime, winnersOf, H, T } from './game.ts';
import { RULES, CHAOS_CARD, stepwiseSetup } from './rules.ts';
import { makeRng, rand, shuffle } from './rng.ts';
import { deckProfile, cardValue, playValue, traitGainBonus, PERM_TURNS, INFO } from './synergy.ts';
import type { DeckProfile } from './synergy.ts';

/** Настраиваемые веса оценки (test/tune.ts подбирает их ареной; значения по умолчанию — результат калибровки) */
export const PARAMS = {
  /** ценность жизней: −lifeScale·exp(−life/lifeDiv) + lifeLin·life (близость смерти + запас жизней как ресурс) */
  lifeScale: 4,
  lifeDiv: 8,
  lifeLin: 0.08,
  /** вес чужих жизней: лидера и остальных; лидерство — мягкое, по ожидаемым очкам (очки + потенциал), масштаб leadTau */
  leaderLifeW: 0.6,
  otherLifeW: 0.35,
  leadTau: 0.7,
  /** чужой игрок в примерке тратит ценную защиту, только если после удара останется меньше defendGuard жизней;
   *  дешёвую (ценность розыгрыша ≤ cheapDef) или защиту с бонусом — всегда */
  defendGuard: 10,
  cheapDef: 1,
  /** 0 — соперник в примерке защищается первой картой от любого урона ≥ probeDefendMin (старое поведение) */
  probeSmartDefend: 1,
  /** ПО за чипсину */
  chipVp: 0.35,
  /** ресурсы хода: мощь и карта на руке */
  powerW: 0.3,
  handW: 0.3,
  /** чипсина за приз в ход */
  prizeW: 0.35,
  /** лидеру выгодна короткая партия: −lead·T·leadT */
  leadT: 0.03,
  /** среднее превосходство над всеми (сверх отрыва от лидера) */
  avgW: 0.25,
  /** добавка, когда конец партии предрешён */
  endBonus: 20,
  /** вес отъёма карты у соперника при покупке */
  denialW: 0.7,
  /** порог ценности покупки */
  buyMin: 0.35,
  /** бонус цели, которую можно добить атаками с руки */
  focusBonus: 1.2,
  /** пересдач скрытой информации при примерке атак */
  attackSamples: 1,
  /** что пересдавать перед примеркой (>0 — да): чужие руки, порядок чужих колод, свою колоду, общие колоды, стопку жетонов */
  maskHands: 1,
  maskRivalDecks: 1,
  maskOwnDeck: 1,
  maskCommon: 1,
  maskDdk: 1,
  /** в примерке чужой игрок защищается от урона ≥ probeDefendMin (0 — всегда, 99 — никогда) */
  probeDefendMin: 3,
  /** карта на руке в чужой ход: keepW·ценность розыгрыша (что она даст в мой ход); 0 — плоская оценка handW */
  keepW: 0.3,
  /** вес опциона защиты (возможность отбить удар до своего хода) */
  optionW: 1,
  /** карта на руке соперника — его ресурс (защита, которую он сожжёт, или удар в его ход): rivalHandW·ценность розыгрыша × вес соперника */
  rivalHandW: 0.3,
  /** априорный темп смертей на игрока за раунд (для оценки оставшихся ходов) */
  deathRate: 0.6,
  /** можно ли выбирать себя целью своей атаки, когда есть соперник (суицид ради чипсин/сброса жизней) */
  allowSelfTarget: 1,
  /** значимость соперника: 0,25 + 0,75·sigmoid((его очки − мои + relevShift)/relevScale); далеко отставших не гнобим */
  relevShift: 4,
  relevScale: 4,
  // ---- рычаги характеров (по умолчанию 0 — поведение прежнее; включаются накладками STYLES) ----
  /** память обид: бонус цели за каждую единицу урона, полученного от неё («мстительный») */
  grudgeW: 0,
  /** детерминированный шум в оценке действий, ПО («псих»: делает странности, оставаясь легальным) */
  noiseW: 0,
  /** стилевые бонусы покупок: за урон и за чипсины карты из базы знаний («агрессор» и «скупердяй») */
  styleBuyDmg: 0,
  styleBuyChips: 0,
};
export type Params = typeof PARAMS;
export function setParams(p: Partial<Params>): void {
  Object.assign(PARAMS, p);
}

/**
 * Характеры ботов: накладки весов поверх одного мозга. Сила должна оставаться сопоставимой с обычным ботом
 * (проверяется ареной), различаться должно поведение — за кем охотится, что покупает, как рискует.
 */
export const STYLES: Record<BotStyle, { label: string; emoji: string; params: Partial<Params> }> = {
  /** охотник: чужие жизни дороже, добивание слаще, защиту бережёт только у края, докупает урон */
  aggressor: { label: 'агрессор', emoji: '🔥', params: { leaderLifeW: 1.1, otherLifeW: 0.7, focusBonus: 2.4, prizeW: 0.5, defendGuard: 6, styleBuyDmg: 0.35 } },
  /** копит чипсины и очки, бережёт жизни, в драку не лезет, покупает при первой возможности */
  hoarder: { label: 'скупердяй', emoji: '💰', params: { chipVp: 0.6, lifeLin: 0.15, leaderLifeW: 0.35, otherLifeW: 0.2, focusBonus: 0.5, defendGuard: 14, buyMin: 0.2, styleBuyChips: 0.35 } },
  /** помнит, кто его бил (Player.grudge), и возвращает сторицей */
  vengeful: { label: 'мстительный', emoji: '🗡️', params: { grudgeW: 0.3, focusBonus: 1.6 } },
  /** шум в оценке: делает странные, но легальные ходы; гнобит кого попало без оглядки на значимость */
  maniac: { label: 'псих', emoji: '🤪', params: { noiseW: 1.5, relevShift: 12, focusBonus: 0.6 } },
};

/** Предел ответов на вложенные запросы в одной примерке (защита от зацикливания) */
const MAX_NESTED = 60;
/** Глубина рекурсии примерок: вложенный запрос самого бота внутри примерки тоже примеряется (один уровень) */
const MAX_DEPTH = 1;

// ---------- оставшиеся ходы и перевод мощи в очки ----------

/** Оценка числа раундов до конца партии (≈ моих оставшихся ходов): по жетонам дохлых колдунов, основной колоде и легендам */
export function remainingTurns(G: Game): number {
  const n = Math.max(1, G.players.length);
  const pile = G.ddkPile ? G.ddkPile.length : G.ddkPileCount ?? RULES.ddkPerPlayer * n;
  const rounds = (G.turnNo ?? 1) / n;
  const used = G.players.reduce((s, p) => s + p.ddk.length, 0);
  // темп смертей: наблюдаемый, сглаженный априорным (≈0,6 смерти на игрока за раунд)
  const rate = (used + 2 * PARAMS.deathRate * n) / (rounds + 2);
  const tDdk = pile / Math.max(0.3, rate);
  const mainLeft = G.mainDeck.length || G.mainDeckCount || 0;
  const legendLeft = G.legendDeck.length || G.legendDeckCount || 0;
  const tMain = mainLeft / (1.3 * n);
  const tLeg = legendLeft / (0.4 * n);
  return Math.max(0.5, Math.min(12, tDdk, tMain, tLeg));
}

/** Сколько ПО принесёт 1 P за ход при T оставшихся ходах (0,3 ПО за мощь, с дисконтом будущих ходов) */
export function potFactor(T: number): number {
  return 0.3 * 0.85 * (1 - Math.pow(0.85, T)) / 0.15;
}

/**
 * Ценность жизней в ПО: чем меньше жизней, тем ближе смерть (−3 ПО за жетон, приз убийце, случайный эффект);
 * плюс линейная часть — запас жизней сам по себе преимущество (буфер против будущих ударов), а не только у полуживого.
 */
function lifeValue(life: number, nearDeath = true): number {
  const l = Math.max(0, life);
  return (nearDeath ? -PARAMS.lifeScale * Math.exp(-l / PARAMS.lifeDiv) : 0) + PARAMS.lifeLin * l;
}

/** Ценность чипсины в ПО: платёжное средство за легенды (и за жетон «−8 за 5 чипсин») */
function chipValue(p: Player, T: number): number {
  let v = T <= 1.5 ? PARAMS.chipVp * 0.45 : PARAMS.chipVp;
  if (p.chips < 5 && p.ddk.some((id) => ddkDef(id).key === 'k16')) v += 0.4;
  return v;
}

/**
 * Значимость соперника (0,25…1): насколько его результат влияет на мой. Кто далеко позади, тому жетоны и урон
 * почти безразличны для итога — бить его ради самого удара незачем (иначе боты всей толпой гнобят отстающего).
 */
export function relevance(vpRival: number, vpMe: number): number {
  const x = (vpRival - vpMe + PARAMS.relevShift) / PARAMS.relevScale;
  return 0.25 + 0.75 / (1 + Math.exp(-x));
}

/** Потенциал колоды в ПО: ожидаемая мощь за ход (рука × средняя ценность карты + постоянки) × оставшиеся ходы */
export function potential(G: Game, p: Player, T: number, prof: DeckProfile = deckProfile(G, p)): number {
  const perTurn = prof.hand * prof.avg + prof.permPerTurn;
  return perTurn * potFactor(T);
}

// ---------- оценка позиции ----------

/** Насколько хороша позиция для игрока pid (в ПО-эквивалентах) */
export function evaluate(G: Game, pid: PlayerId): number {
  const scores = G.players.map((p) => scorePlayer(G, p).total);
  const myVp = scores[pid] ?? 0;
  const rivals = G.players.filter((p) => p.id !== pid);
  const bestRival = Math.max(...rivals.map((p) => scores[p.id] ?? 0));
  const lead = myVp - bestRival;
  if (G.phase === 'ended') return (winnersOf(G).includes(pid) ? 1000 : -1000) + lead;
  const me = player(G, pid);
  const T = remainingTurns(G);
  const nr = Math.max(1, rivals.length);
  const profiles = new Map(G.players.map((p) => [p.id, deckProfile(G, p)]));
  const profOf = (p: Player): DeckProfile => profiles.get(p.id) ?? deckProfile(G, p);
  // очки: отрыв от лидера + среднее превосходство над всеми (отставшие — с малым весом)
  const rel = new Map(rivals.map((r) => [r.id, relevance(scores[r.id] ?? 0, myVp)]));
  const relOf = (r: Player): number => rel.get(r.id) ?? 1;
  let v = lead + PARAMS.avgW * rivals.reduce((s, r) => s + (myVp - (scores[r.id] ?? 0)) * relOf(r), 0) / nr;
  // потенциал колод
  const myPot = potential(G, me, T, profOf(me));
  const pots = rivals.map((r) => potential(G, r, T, profOf(r)));
  v += myPot - Math.max(...pots) + PARAMS.avgW * pots.reduce((s, x) => s + (myPot - x), 0) / nr;
  // жизни: свои полностью, чужие — с весом: чем ближе соперник к лидерству по ожидаемым очкам (очки + потенциал
  // колоды), тем дороже его жизни. Близость соперника к смерти считается только когда добить его реально в этот ход
  // (остаток урона на руке): иначе жетон ему всё равно даст кто-то другой, а приз достанется тому — недобивающий удар
  // по полуживому лишь подарок следующему атакующему, и бить надо сильного
  const myTurn = G.phase === 'play' && G.turn?.player === pid;
  v += lifeValue(me.life);
  const expScore = rivals.map((r, i) => (scores[r.id] ?? 0) + (pots[i] ?? 0));
  const topExp = Math.max(...expScore);
  const reach = myTurn ? handDamage(me) : 0;
  rivals.forEach((r, i) => {
    const leadness = Math.exp(((expScore[i] ?? 0) - topExp) / PARAMS.leadTau);
    const w = (PARAMS.otherLifeW + (PARAMS.leaderLifeW - PARAMS.otherLifeW) * leadness) * relOf(r);
    v -= lifeValue(r.life, r.life <= reach) * w;
    // его рука — тоже ресурс: заставить сбросить карту или сжечь защиту на мой удар — выигрыш, а не «атака пропала».
    // Только для сложного бота: он видит настоящую руку; обычный бот с чужой рукой не взаимодействует вовсе
    if (PARAMS.rivalHandW > 0 && me.botLevel === 'cheat') {
      const rp = profOf(r);
      v -= PARAMS.rivalHandW * w * r.hand.reduce((acc, c) => acc + Math.max(0, playValue(card(c), rp)), 0);
    }
  });
  // чипсины
  v += me.chips * chipValue(me, T);
  for (const r of rivals) v -= r.chips * 0.1;
  // ресурсы хода: мощь и карты на руке — по тому, что они дадут при розыгрыше в свой ход
  if (myTurn && G.turn) v += G.turn.power * PARAMS.powerW + me.hand.length * PARAMS.handW;
  else {
    // в чужой ход: карта на руке стоит того, что даст при розыгрыше в мой ход; карта защиты — ещё и возможность
    // отбить удар, который может прилететь до моего хода (защищаться ценной картой от слабого удара невыгодно)
    const myProf = profOf(me);
    v += PARAMS.keepW > 0
      ? me.hand.reduce((s, c) => s + Math.max(0, playValue(card(c), myProf)), 0) * PARAMS.keepW
      : me.hand.reduce((s, c) => s + (card(c).defense ? 0.6 : 0.1), 0);
    const defs = me.hand.filter((c) => card(c).defense).length;
    if (defs && PARAMS.optionW > 0) v += PARAMS.optionW * defenseOption(G, me, rivals.map((r) => [r, profOf(r)] as const)) * (1 + 0.4 * (defs - 1));
  }
  // главный приз: +1 чипсина за каждый свой ход
  const prizeVal = PARAMS.prizeW * Math.min(T, 6);
  if (G.prizeOwner === pid) v += prizeVal;
  else if (G.prizeOwner != null) v -= prizeVal * 0.5;
  // конец партии предрешён (жетоны кончились / триггер конца): почти терминальная оценка
  const pile = G.ddkPile ? G.ddkPile.length : G.ddkPileCount ?? 1;
  if (G.endTriggered || pile === 0) v += lead > 0 ? PARAMS.endBonus : lead < 0 ? -PARAMS.endBonus : 0;
  // лидеру выгодна короткая партия, отстающему — длинная
  v -= lead * T * PARAMS.leadT;
  return v;
}

/**
 * Ценность возможности защититься до своего хода: сумма по соперникам, которые ещё сходят до меня, ожидаемого
 * урона их колоды (доля атак × их урон, разделённый между врагами) в пересчёте на мои жизни сейчас.
 */
function defenseOption(G: Game, me: Player, rivals: readonly (readonly [Player, DeckProfile])[]): number {
  if (!G.turn) return 0;
  const n = G.players.length;
  let v = 0;
  // соперники по часовой от текущего игрока до меня (включая текущего, если его ход ещё идёт)
  for (const [r, prof] of rivals) {
    const toMe = (me.id - r.id + n) % n;
    const toCur = (G.turn.player - r.id + n) % n;
    if (toCur !== 0 && toMe < toCur) continue; // уже сходил в этом круге
    const dmg = (7 * prof.supply['damage']) / Math.max(1, n - 1);
    if (dmg <= 0) continue;
    const pAttack = Math.min(0.9, prof.supply['damage'] * 0.6);
    v += pAttack * (lifeValue(me.life) - lifeValue(me.life - dmg)) * 0.7;
  }
  return v;
}

// ---------- примерки ----------

/** Облегчённая копия для примерок: журнал и события боту не нужны, а копируются они дорого */
function lite(G: Game): Game {
  const g: Game = { ...G, log: [], events: [], journal: [] };
  if (G.pending) g.pending = { ...G.pending, base: { ...G.pending.base, log: [], events: [], journal: [] } };
  return g;
}

/**
 * Пересдать скрытую информацию с точки зрения игрока pid: чужие руки возвращаются в колоды и набираются заново,
 * порядок всех колод и стопки жетонов перемешивается. Детерминировано: сид — из состояния ГСЧ и номера пересдачи.
 */
function mask(g: Game, pid: PlayerId, sample: number): void {
  if (!g.rng) return;
  const rng = makeRng((g.rng.s ^ 0x9e3779b9 ^ (pid * 7919 + sample * 104729)) >>> 0);
  for (const p of g.players) {
    if (p.id === pid) {
      if (PARAMS.maskOwnDeck > 0) p.deck = shuffle(rng, p.deck);
      continue;
    }
    if (PARAMS.maskHands > 0) {
      const n = p.hand.length;
      p.deck.push(...p.hand);
      p.hand = [];
      p.deck = shuffle(rng, p.deck);
      for (let i = 0; i < n; i++) {
        const c = p.deck.pop();
        if (c) p.hand.push(c);
      }
    } else if (PARAMS.maskRivalDecks > 0) p.deck = shuffle(rng, p.deck);
  }
  if (PARAMS.maskCommon > 0) {
    g.mainDeck = shuffle(rng, g.mainDeck);
    g.legendDeck = shuffle(rng, g.legendDeck);
  }
  if (PARAMS.maskDdk > 0 && g.ddkPile) g.ddkPile = shuffle(rng, g.ddkPile);
}

/** Все iid карт, упомянутых в текущем запросе: их положение уже показано игроку и пересдаче не подлежит */
function promptIids(g: Game): Set<Iid> {
  const out = new Set<Iid>();
  const pr = g.prompt as unknown as Record<string, unknown> | null;
  if (!pr) return out;
  const walk = (v: unknown): void => {
    if (Array.isArray(v)) {
      for (const x of v) walk(x);
      return;
    }
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      if (typeof o.iid === 'number') out.add(o.iid);
      for (const x of Object.values(o)) walk(x);
      return;
    }
    if (typeof v === 'number') return; // голые числа (Iid[] в chooseCards) добираем ниже
  };
  walk(pr);
  const cards = (pr.cards ?? null) as unknown;
  if (Array.isArray(cards)) for (const c of cards) if (typeof c === 'number') out.add(c);
  return out;
}

/**
 * Пересдать скрытое в готовом (уже восстановленном) состоянии посреди эффекта: чужие руки и колоды.
 * Свою колоду и общие колоды здесь не трогаем — на них мог ссылаться уже сформированный запрос
 * («посмотри 5 верхних карт колоды легенд»), а перетасовка сделала бы его несогласованным.
 */
function maskRivalsLive(g: Game, pid: PlayerId, sample: number): void {
  if (!g.rng) return;
  const keep = promptIids(g);
  const rng = makeRng((g.rng.s ^ 0x9e3779b9 ^ (pid * 7919 + sample * 104729)) >>> 0);
  for (const p of g.players) {
    if (p.id === pid) continue;
    // карты, на которые ссылается запрос, остаются на своих местах; остальное у этого соперника пересдаётся
    const fixedHand = p.hand.filter((c) => keep.has(c.iid));
    const fixedDeck = p.deck.filter((c) => keep.has(c.iid));
    const freeHand = p.hand.filter((c) => !keep.has(c.iid));
    const freeDeck = p.deck.filter((c) => !keep.has(c.iid));
    if (PARAMS.maskHands > 0) {
      const n = freeHand.length;
      const pool = shuffle(rng, [...freeDeck, ...freeHand]);
      const hand: CardInst[] = [];
      for (let i = 0; i < n; i++) {
        const c = pool.pop();
        if (c) hand.push(c);
      }
      p.hand = [...fixedHand, ...hand];
      p.deck = [...fixedDeck, ...pool];
    } else if (PARAMS.maskRivalDecks > 0) {
      p.deck = [...fixedDeck, ...shuffle(rng, freeDeck)];
    }
  }
}

/**
 * Копия состояния с живым (переигранным) незавершённым эффектом и пересданной скрытой информацией.
 * Посреди эффекта состояние сначала восстанавливается (эффект доигрывается до текущего запроса), и только
 * потом пересдаются чужие руки и колоды: пересдавать снимок ДО эффекта можно лишь пока он не потратил
 * случайность, а это условие отключало честность как раз в самых интересных местах — например, при выборе,
 * с чьей колоды сыграть верхнюю карту, честный бот видел, у кого сверху постоянка.
 */
function fork(G: Game, pid: PlayerId, sample = 0): Game {
  const g = deepClone(lite(G));
  g.probe = true; // примерка: движок не пишет побочные заметки (мотивы ботов)
  if (g.pending) g.pending.base.probe = true; // restoreRuntime переигрывает со снимка — флаг должен пережить
  // сложный бот подглядывает: примеряет на настоящем состоянии
  const honest = player(G, pid).botLevel !== 'cheat';
  if (!G.pending) {
    g.pending = null;
    if (honest) mask(g, pid, sample);
    return g;
  }
  const out = restoreRuntime(g);
  if (honest) maskRivalsLive(out, pid, sample);
  return out;
}

/** Довести копию до конца эффекта, отвечая на вложенные запросы: свои — примеркой (до глубины), чужие — дешёвой политикой */
function settle(g: Game, pid: PlayerId, depth: number): void {
  for (let i = 0; i < MAX_NESTED && g.prompt; i++) {
    const who = promptWho(g);
    if (who == null) break;
    const pr = g.prompt;
    const ans = who === pid && depth < MAX_DEPTH && (pr.type === 'chooseOption' || pr.type === 'choosePlayer' || pr.type === 'defend')
      ? answerFor(g, pid, pr, depth + 1)
      : defaultAnswer(g, who, pr);
    if (!applyAction(g, who, { name: 'answer', args: [ans] }).ok) break;
  }
}

/**
 * Доиграть свой ход дешёвой политикой: все карты с руки (сначала без атаки), все обычные активации, покупки по
 * статической модели. Так примерка оценивает действие по итогу хода, а не по мгновенному эффекту: видно, что добор
 * даст мощь на покупку, что Лохолох надо активировать после всех карт, что Вурдалакар — первым.
 * Ситуативные активации (уничтожить карту ради разового эффекта) сами по себе не делаются — их бот примеряет отдельно.
 */
function rolloutTurn(g: Game, pid: PlayerId): void {
  for (let i = 0; i < 40; i++) {
    if (g.phase !== 'play' || g.prompt || !g.turn || g.turn.player !== pid) return;
    const p = player(g, pid);
    const c = p.hand.find((x) => !card(x).attack) ?? p.hand[0];
    let a: Action | null = null;
    if (c) a = { name: 'playCard', args: [c.iid] };
    else {
      const act = controlled(g, p).find((x) => canActivate(g, x) && !T.SITUATIONAL_ACTIVATE.has(x.cid));
      const tok = act ? null : H.tokenActivations(g, p).find((x) => x.enabled !== false);
      if (act) a = { name: 'activateCard', args: [act.iid] };
      else if (tok) a = { name: 'activateCard', args: [tok.id] };
      else a = bestBuy(g, p);
    }
    if (!a || !applyAction(g, pid, a).ok) return;
    settle(g, pid, MAX_DEPTH);
  }
}

/**
 * Оценка после действия на копии; null — действие невозможно. samples — усреднить по нескольким пересдачам.
 * На глубине 0 копия доигрывается до конца хода (rolloutTurn), чтобы сравнивать итоги хода, а не мгновенные эффекты.
 */
function tryAction(G: Game, pid: PlayerId, a: Action, depth = 0, samples = 1): number | null {
  let sum = 0;
  for (let k = 0; k < samples; k++) {
    const g = fork(G, pid, k);
    if (!applyAction(g, pid, a).ok) return null;
    settle(g, pid, depth);
    if (depth === 0) rolloutTurn(g, pid);
    sum += evaluate(g, pid);
  }
  return sum / samples + (PARAMS.noiseW > 0 ? PARAMS.noiseW * jitter(G, a) : 0);
}

/** Детерминированный шум оценки для «психа»: стабилен для пары (состояние, действие), разный у разных действий */
function jitter(G: Game, a: Action): number {
  const s = JSON.stringify(a);
  let h = (G.rng?.s ?? 0) ^ Math.imul(s.length + 1, 2654435761);
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return rand(makeRng(h >>> 0));
}

/** Оценка действия по итогу хода (та же примерка, что у решений бота) — для послематчевого разбора (review.ts) */
export function assessAction(G: Game, pid: PlayerId, a: Action): number | null {
  return tryAction(G, pid, a);
}

/** Оценка позиции «если ничего особенного не делать»: ход доигрывается дешёвой политикой */
function baseline(G: Game, pid: PlayerId): number {
  const g = fork(G, pid, 0);
  rolloutTurn(g, pid);
  return evaluate(g, pid);
}

function tryAnswer(G: Game, pid: PlayerId, ans: PromptAnswer, depth = 0, samples = 1): number | null {
  return tryAction(G, pid, { name: 'answer', args: [ans] }, depth, samples);
}

// ---------- добивание ----------

/** Ожидаемый урон одной цели от атак на руке (грубо, по базе знаний; «каждому врагу» тоже считается) */
function handDamage(p: Player): number {
  let d = 0;
  for (const c of p.hand) {
    const def = card(c);
    if (!def.attack) continue;
    d += INFO[def.id]?.dmg ?? 0;
  }
  return d;
}

/**
 * Кого стоит добить: враг, чьи жизни не больше урона, который бот может сосредоточить с руки. Убийство — это жетон
 * жертве (−3 ПО), главный приз и чипсины/эффекты «если подох»; отдельные удары по нему выглядят слабо, если не видеть
 * итог, — поэтому такой цели даётся бонус при выборе цели. Среди убиваемых — лидер, иначе самый хилый.
 */
export function focusTarget(G: Game, pid: PlayerId): PlayerId | null {
  const me = player(G, pid);
  const now = G.prompt?.type === 'choosePlayer' && G.prompt.damage ? Math.max(0, ...Object.values(G.prompt.damage)) : 0;
  const dmg = handDamage(me) + now;
  if (dmg <= 0) return null;
  // добивать стоит претендента на победу; далеко отставшего — незачем
  const rivals = G.players.filter((p) => p.id !== pid && p.life <= dmg * 0.9 && contender(G, p.id));
  if (!rivals.length) return null;
  const scores = new Map(rivals.map((r) => [r.id, scorePlayer(G, r).total]));
  const top = Math.max(...scores.values());
  const leaders = rivals.filter((r) => scores.get(r.id) === top);
  return leaders.reduce((a, b) => (b.life < a.life ? b : a)).id;
}

/**
 * Цель атаки обычного бота — простое правило вместо примерки (требование пользователя: обычные боты не должны
 * «гнобить непричастных» и разменивать удар на воображаемые карты в чужой руке): бьём того, кого можно добить в этот
 * ход (среди таких — лидера по очкам), иначе лидера по очкам. При равных очках — примеркой между ними.
 */
function plainTarget(G: Game, pid: PlayerId, pr: ChoosePlayerPrompt, pool: readonly PlayerId[], depth: number): PlayerId | null {
  const rivals = pool.filter((c) => c !== pid);
  if (!rivals.length) return null;
  // ранг цели: очки + память обид («мстительный» бот среди равных бьёт обидчика, при большой обиде — и не лидера)
  const vp = (c: PlayerId): number => scorePlayer(G, player(G, c)).total + PARAMS.grudgeW * (player(G, pid).grudge?.[c] ?? 0) + (PARAMS.noiseW > 0 ? PARAMS.noiseW * jitter(G, { name: 'answer', args: [c] }) : 0);
  const pick = (group: readonly PlayerId[]): PlayerId | null => {
    const top = Math.max(...group.map(vp));
    const leaders = group.filter((c) => vp(c) === top);
    if (leaders.length === 1) return leaders[0] ?? null;
    return best(leaders, (c) => tryAnswer(G, pid, c, depth))?.item ?? leaders[0] ?? null;
  };
  const leader = pick(rivals);
  const killable = rivals.filter((c) => canKill(G, pid, pr, c));
  const kill = killable.length ? pick(killable) : null;
  if (kill == null || kill === leader) return leader;
  // последний жетон: добивание заканчивает партию — только если по примерке это не хуже удара по лидеру
  // (отстающему конец партии — поражение, ему выгоднее бить лидера)
  if ((G.ddkPile?.length ?? 0) <= 1 && leader != null && (tryAnswer(G, pid, kill, depth) ?? -Infinity) < (tryAnswer(G, pid, leader, depth) ?? -Infinity)) return leader;
  return kill;
}

/** Можно ли добить цель в этот ход: урон этой карты плюс остальные атаки на руке */
function canKill(G: Game, pid: PlayerId, pr: ChoosePlayerPrompt, c: PlayerId): boolean {
  return player(G, c).life <= (pr.damage?.[c] ?? 0) + handDamage(player(G, pid));
}

/**
 * Кандидаты в цели атаки без добиваемых аутсайдеров (для обоих уровней бота, до примерки): убийство того, кто не
 * претендует на победу, не меняет, кто выигрывает, — оно лишь тратит жетон из общей стопки (приближает конец партии,
 * что на руку лидеру) и отнимает у отстающего удовольствие от игры, а лидер тем временем остаётся нетронутым.
 * Исключение — последний жетон: добивание заканчивает партию, и это решает примерка (лидеру — победа).
 */
function attackPool(G: Game, pid: PlayerId, pr: ChoosePlayerPrompt, pool: readonly PlayerId[]): readonly PlayerId[] {
  if (!pr.damage || (G.ddkPile?.length ?? 0) <= 1) return pool;
  const keep = pool.filter((c) => c === pid || !canKill(G, pid, pr, c) || contender(G, c));
  return keep.length ? keep : pool;
}

/**
 * Претендент на победу: его очки не дальше relevShift от лучшего счёта за столом (моего или чужого) по той же
 * `relevance`, что и в оценке позиции. Добивать стоит только его: убийство отставшего не меняет, кто выигрывает, —
 * оно лишь тратит жетон из общей стопки (приближает конец партии, что на руку лидеру) и отнимает у отстающего
 * удовольствие от игры; удар по лидеру в тот же момент хотя бы сокращает отрыв.
 */
export function contender(G: Game, victim: PlayerId): boolean {
  const top = Math.max(...G.players.map((p) => scorePlayer(G, p).total));
  return relevance(scorePlayer(G, player(G, victim)).total, top) >= 0.5;
}

function best<T>(items: readonly T[], score: (x: T) => number | null): { item: T; score: number } | null {
  let res: { item: T; score: number } | null = null;
  for (const item of items) {
    const s = score(item);
    if (s == null) continue;
    if (!res || s > res.score) res = { item, score: s };
  }
  return res;
}

// ---------- политики выбора карт ----------

interface Cand {
  iid: Iid;
  def: CardDef;
  /** ценность карты в P в контексте колоды */
  p: number;
  /** очки на карте (с условными) */
  vp: number;
  inHand: boolean;
}

function candidates(p: Player, pr: ChooseCardsPrompt, prof: DeckProfile): Cand[] {
  return pr.defs.map((d) => {
    const def = card(d.cid);
    const v = cardValue(def, prof);
    return { iid: d.iid, def, p: v.p, vp: v.vp, inHand: p.hand.some((x) => x.iid === d.iid) };
  });
}

/**
 * Что уничтожить: только пустышки и минусовые карты — вялые палочки, Пшики, затем Знаки и прочие слабые карты без очков,
 * если они хуже среднего в колоде. Карты с очками не уничтожаются никогда, кроме принудительного выбора
 * (min > 0 и другого нет) — тогда самые дешёвые по очкам.
 */
export function pickTrash(G: Game, p: Player, pr: ChooseCardsPrompt, prof: DeckProfile = deckProfile(G, p)): Iid[] {
  const cs = candidates(p, pr, prof);
  const myTurn = G.turn?.player === p.id;
  // в свой ход карта на руке ещё сыграет: уничтожать её дороже, чем такую же из сброса
  const cost = (c: Cand): number => c.p + (myTurn && c.inHand ? c.def.power : 0) + (PARAMS.noiseW > 0 ? PARAMS.noiseW * 0.3 * jitter(G, { name: 'answer', args: [c.iid] }) : 0);
  const junk = cs
    .filter((c) => c.vp <= 0 && !c.def.permanent && cost(c) < Math.max(0.9, 0.75 * prof.avg))
    .sort((a, b) => a.vp - b.vp || cost(a) - cost(b));
  const take = Math.min(pr.max, junk.length);
  const res = junk.slice(0, take).map((c) => c.iid);
  if (res.length < pr.min) {
    const rest = cs.filter((c) => !res.includes(c.iid)).sort((a, b) => a.vp - b.vp || cost(a) - cost(b));
    for (const c of rest) {
      if (res.length >= pr.min) break;
      res.push(c.iid);
    }
  }
  return res;
}

/** Что сбросить: самые слабые; в чужой ход карты защиты дороже всего */
export function pickDiscard(G: Game, p: Player, pr: ChooseCardsPrompt, prof: DeckProfile = deckProfile(G, p)): Iid[] {
  const cs = candidates(p, pr, prof);
  const myTurn = G.turn?.player === p.id;
  const keep = (c: Cand): number => c.p + (!myTurn && c.def.defense ? 2 : 0) + (c.def.permanent ? c.vp : 0) + (PARAMS.noiseW > 0 ? PARAMS.noiseW * 0.3 * jitter(G, { name: 'answer', args: [c.iid] }) : 0);
  return cs.sort((a, b) => keep(a) - keep(b)).slice(0, Math.max(pr.min, Math.min(pr.max, pr.min))).map((c) => c.iid);
}

/** Дешёвая политика для вложенных запросов внутри примерки (без рекурсии) */
export function defaultAnswer(G: Game, who: PlayerId, pr: Prompt): PromptAnswer {
  switch (pr.type) {
    case 'info': return null;
    case 'defend': {
      // чужой игрок в примерке защищается как разумный: от 1–2 урона карту бережёт; дешёвую карту (или защиту с
      // бонусом) тратит всегда, ценную — только если после удара жизней останется мало
      const me = player(G, who);
      if (pr.damage != null && pr.damage < PARAMS.probeDefendMin && me.life > pr.damage + 3) return null;
      const opts = pr.cards.map((iid) => me.hand.find((c) => c.iid === iid)).filter((c): c is CardInst => !!c);
      if (!opts.length || PARAMS.probeSmartDefend <= 0) return pr.cards[0] ?? null;
      const prof = deckProfile(G, me);
      const cheapest = opts.reduce((a, b) => (playValue(card(b), prof) < playValue(card(a), prof) ? b : a));
      const bonus = opts.find((c) => (INFO[card(c).id]?.def ?? 0) > 0);
      if (bonus) return bonus.iid;
      if (playValue(card(cheapest), prof) <= PARAMS.cheapDef) return cheapest.iid;
      if (pr.damage == null || me.life - pr.damage < PARAMS.defendGuard) return cheapest.iid;
      return null;
    }
    case 'chooseNumber': return pr.max;
    case 'chooseOption': return defaultOption(pr.options.map((o) => o.id));
    case 'choosePlayer': {
      const others = pr.candidates.filter((id) => id !== who);
      if (!others.length) return pr.candidates[0] ?? null;
      // цель атаки: добиваемый враг, иначе лидер по очкам
      if (pr.damage || pr.harm) {
        const f = focusTarget(G, who);
        if (f != null && others.includes(f)) return f;
      }
      return others.reduce((b, id) => (scorePlayer(G, player(G, id)).total > scorePlayer(G, player(G, b)).total ? id : b));
    }
    case 'chooseCards': {
      const p = player(G, who);
      if (pr.kind === 'trash') return pickTrash(G, p, pr);
      if (pr.kind === 'discard') return pickDiscard(G, p, pr);
      if (pr.kind === 'asLegend') return []; // считать фамильяров легендами во вред себе — незачем
      const prof = deckProfile(G, p);
      const cs = candidates(p, pr, prof).sort((a, b) => b.p + b.vp * 3 - (a.p + a.vp * 3));
      return cs.slice(0, Math.max(pr.min, Math.min(pr.max, 1))).map((c) => c.iid);
    }
  }
}

/** Разумный вариант по умолчанию: «+мощь» и «да» предпочтительнее уничтожения/отказа */
function defaultOption(ids: string[]): string | null {
  const pref = ['power', 'yes', 'attack', 'supply', 'victim', 'discard', 'deckTop', 'hand', 'life'];
  for (const id of pref) if (ids.includes(id)) return id;
  return ids[0] ?? null;
}

// ---------- ответы на запросы ----------

/** Отладка: оценка каждого варианта ответа на выбор игрока (как в answerFor), с бонусом добивания */
export function debugAnswers(G: Game, pid: PlayerId, pr: Prompt): { ans: PromptAnswer; score: number | null; focus: boolean }[] {
  if (pr.type !== 'choosePlayer') return [];
  const focus = pr.damage ? focusTarget(G, pid) : null;
  const samples = pr.damage ? PARAMS.attackSamples : 1;
  return pr.candidates.map((c) => ({ ans: c, score: tryAnswer(G, pid, c, 0, samples), focus: c === focus }));
}

export function answerFor(G: Game, pid: PlayerId, pr: Prompt, depth = 0): PromptAnswer {
  switch (pr.type) {
    case 'info': return null;
    case 'chooseOption': return best(pr.options, (o) => tryAnswer(G, pid, o.id, depth))?.item.id ?? defaultAnswer(G, pid, pr);
    case 'choosePlayer': {
      const attack = !!pr.damage;
      // вредный выбор без урона (голосование за лошару и т.п.) — целимся как при атаке, себя не выбираем
      const harm = attack || !!pr.harm;
      const pool0 = harm && PARAMS.allowSelfTarget <= 0 && pr.candidates.some((c) => c !== pid) ? pr.candidates.filter((c) => c !== pid) : pr.candidates;
      const pool = attackPool(G, pid, pr, pool0);
      if (harm && player(G, pid).botLevel !== 'cheat') {
        const t = plainTarget(G, pid, pr, pool, depth);
        if (t != null) return t;
      }
      const cands: PromptAnswer[] = [...pool, ...(pr.optional ? [null] : [])];
      // выбор цели атаки: скрытая информация (защита в чужой руке) — усреднить по пересдачам; добиваемой цели — бонус
      const focus = attack ? focusTarget(G, pid) : null;
      const samples = attack && depth === 0 ? PARAMS.attackSamples : 1;
      // при прочих равных (урон 1–2 всем одинаково безразличен; один голос ничего не решает) — цель с бо́льшими очками
      const tie = (c: PlayerId): number => (harm && c !== pid ? 0.002 * scorePlayer(G, player(G, c)).total : 0);
      // память обид («мстительный»): цель тем слаще, чем больше урона от неё получено
      const grudge = (c: PlayerId): number => (harm && c !== pid ? PARAMS.grudgeW * (player(G, pid).grudge?.[c] ?? 0) : 0);
      return best(cands, (c) => {
        const s = tryAnswer(G, pid, c, depth, samples);
        return s == null ? null : s + (c != null && c === focus ? PARAMS.focusBonus : 0) + (typeof c === 'number' ? tie(c) + grudge(c) : 0);
      })?.item ?? defaultAnswer(G, pid, pr);
    }
    case 'defend': {
      const cands: PromptAnswer[] = [...pr.cards, null];
      return best(cands, (c) => tryAnswer(G, pid, c, depth))?.item ?? null;
    }
    case 'chooseNumber': {
      const span = pr.max - pr.min;
      const cands = span <= 8 ? Array.from({ length: span + 1 }, (_, i) => pr.min + i) : [...new Set([pr.min, Math.floor((pr.min + pr.max) / 2), pr.max])];
      return best(cands, (n) => tryAnswer(G, pid, n, depth))?.item ?? pr.max;
    }
    case 'chooseCards': {
      const p = player(G, pid);
      if (pr.kind === 'trash') return pickTrash(G, p, pr);
      if (pr.kind === 'discard') return pickDiscard(G, p, pr);
      // прочие выборы (взять тварь из сброса, сыграть карту врага, получить легенду…): ценность каждой карты примеркой
      const none = tryAnswer(G, pid, [], depth) ?? -Infinity;
      const prof = deckProfile(G, p);
      const pool = candidates(p, pr, prof).sort((a, b) => b.p + b.vp * 3 - (a.p + a.vp * 3)).slice(0, 8);
      const scored = pool.map((c) => ({ iid: c.iid, s: (tryAnswer(G, pid, [c.iid], depth) ?? -Infinity) - none })).sort((a, b) => b.s - a.s);
      const positive = scored.filter((x) => x.s > 0.01).length;
      const take = Math.max(pr.min, Math.min(pr.max, positive));
      return scored.slice(0, take).map((x) => x.iid);
    }
  }
}

// ---------- подготовка ----------

/** Ценность жетона свойства «сама по себе» (в P за партию). Порядок сверен контрфактикой
 *  (tools/traits.ts, 112 точек × 10 свойств: prize +10%, treasureDiscount +4%, act6/beastTop/act10 −3.5…−4%) */
const TRAIT_SCORE: Record<TraitKey, number> = {
  prize: 7, threeFamiliars: 6.5, treasureDiscount: 5, hrenalochka: 4.5, permChip: 3.5, wizardChip: 3.2, act6: 2.4, act10: 2.3, beastTop: 2.2, spellHand: 2,
};

function decideSetup(G: Game, p: Player): Action | null {
  const setup = setupOf(G);
  const prof = deckProfile(G, p);
  const famValue = (cid: string): number => {
    const v = cardValue(card(cid), prof);
    return v.p + v.vp * 3;
  };
  // связки жетона и фамильяра
  const bonus = (key: TraitKey, f: string): number =>
    (key === 'hrenalochka' && f === 'malchik-kotoryj-vyzhral' ? 2 : 0) +
    (key === 'act6' && (f === 'schastlivyj-viabu' || f === 'konduktor-zhmi-na-tormoza') ? 1 : 0);
  const offers = setup.traitOffers[p.id] ?? [];
  const fams = setup.familiarOffers[p.id] ?? [];
  if (stepwiseSetup(G)) {
    // по правилам жетон выбирается вслепую, фамильяр — уже под выбранный жетон
    if (!p.traitId) {
      const traitId = best(offers, (t) => TRAIT_SCORE[traitDef(t).key])?.item;
      if (!traitId) throw new Error(`Боту ${p.name} нечего выбирать в подготовке`);
      return { name: 'setupChoose', args: [{ traitId, familiar: null }] };
    }
    if (!setupDone(p)) {
      const key = traitDef(p.traitId).key;
      const f = best(fams, (x) => famValue(x) + bonus(key, x))?.item;
      if (!f) throw new Error(`Боту ${p.name} некого выбрать фамильяром`);
      return { name: 'setupFamiliar', args: [f] };
    }
  }
  if (p.traitId) {
    if (!(setup.third ?? []).includes(p.id)) return null;
    return { name: 'setupThird', args: [best(setup.freeFamiliars, famValue)?.item ?? null] };
  }
  let bestChoice: { traitId: string; familiar: string | null; s: number } | null = null;
  for (const traitId of offers) {
    const key = traitDef(traitId).key;
    const three = !!H.traitPassive({ ...p, traitId })?.threeFamiliars;
    if (three) {
      const s = TRAIT_SCORE[key] + fams.reduce((acc, f) => acc + famValue(f) * 0.8, 0);
      if (!bestChoice || s > bestChoice.s) bestChoice = { traitId, familiar: null, s };
      continue;
    }
    for (const f of fams) {
      const s = TRAIT_SCORE[key] + famValue(f) + bonus(key, f);
      if (!bestChoice || s > bestChoice.s) bestChoice = { traitId, familiar: f, s };
    }
  }
  if (!bestChoice) throw new Error(`Боту ${p.name} нечего выбирать в подготовке`);
  return { name: 'setupChoose', args: [{ traitId: bestChoice.traitId, familiar: bestChoice.familiar }] };
}

// ---------- покупки ----------

export interface Offer {
  c: CardInst | null;
  def: CardDef;
  price: number;
  chipPay: number;
  action: Action;
  marketChips: number;
  familiar: boolean;
}

/** Прирост в ПО-эквивалентах от получения карты def игроком с профилем prof (при T оставшихся ходах) */
export function gainFor(def: CardDef, prof: DeckProfile, T: number, delayTurns: number, traitKey: TraitKey | null): number {
  const v = cardValue(def, prof);
  const F = potFactor(Math.max(0.5, T - delayTurns));
  let g = v.vp;
  if (def.permanent) {
    // постоянка: сыграл один раз — работает каждый ход; в модели среднего её «розыгрышная» часть — прочее
    const info = INFO[def.id] ?? {};
    const perTurn = (info.perTurn ?? 0) + (info.vpPerTurn ?? 0) * 3 * 0.6;
    const rest = v.p - perTurn * PERM_TURNS;
    g += perTurn * F + prof.hand * (rest - prof.avg) / (prof.n + 1) * F * 0.5;
  } else {
    g += prof.hand * (v.p - prof.avg) / (prof.n + 1) * F;
  }
  g += traitGainBonus(traitKey, def) * F * 0.5;
  return g;
}

/** Все доступные покупки с их ценой (мощь + чипсины на легенды); экспорт — для tools/branches.ts */
export function offers(G: Game, p: Player): Offer[] {
  const turn = turnOf(G);
  const res: Offer[] = [];
  for (const c of [...G.market, ...G.legendMarket, ...p.familiars]) {
    const def = card(c);
    const price = priceFor(G, p, c);
    const chipPay = chipsPayable(p, c) ? Math.min(p.chips, Math.max(0, price - turn.power)) : 0;
    if (turn.power + chipPay < price) continue;
    res.push({ c, def, price, chipPay, action: { name: 'buyCard', args: [c.iid, chipPay] }, marketChips: G.marketChips[c.iid] ?? 0, familiar: def.type === 'фамильяр' });
  }
  if (G.chaosPile.length && turn.power >= RULES.chaosPrice) {
    res.push({ c: null, def: card(CHAOS_CARD), price: RULES.chaosPrice, chipPay: 0, action: { name: 'buyChaos', args: [] }, marketChips: 0, familiar: false });
  }
  return res;
}

/** Вероятность, что соперник сможет купить карту в свой следующий ход */
function affordability(G: Game, r: Player, prof: DeckProfile, c: CardInst | null, def: CardDef): number {
  if (!c) return 1;
  const price = priceFor(G, r, c);
  const budget = prof.turnPower + (chipsPayable(r, c) ? r.chips : 0) + 1.5;
  void def;
  return Math.max(0, Math.min(1, (budget - price) / 3));
}

/** Что купить на оставшуюся мощь; null — ничего стоящего */
function bestBuy(G: Game, p: Player): Action | null {
  const T = remainingTurns(G);
  const all = offers(G, p);
  if (!all.length) return null;
  const prof = deckProfile(G, p);
  const traitKey = prof.traitKey;
  const delay = (p.deck.length + p.hand.length + p.discard.length) / prof.hand / 2;
  const myScore = scorePlayer(G, p).total + potential(G, p, T, prof);
  const cv = chipValue(p, T);
  // ценность каждой покупки для меня
  const mine = all.map((o) => {
    let g = gainFor(o.def, prof, T, delay, traitKey) + o.marketChips * cv - o.chipPay * cv * 1.1;
    // фамильяр доступен только мне — спешить некуда; по журналам покупка фамильяра за 6 не окупается лучше карт барахолки
    if (PARAMS.styleBuyDmg > 0 || PARAMS.styleBuyChips > 0) {
      const info = INFO[o.def.id] ?? {};
      g += PARAMS.styleBuyDmg * (info.dmg ?? 0) + PARAMS.styleBuyChips * (info.chips ?? 0);
    }
    return g;
  });
  // отъём: насколько карта усилила бы соперника сверх его лучшей альтернативы
  const denial = all.map(() => 0);
  for (const r of G.players) {
    if (r.id === p.id) continue;
    const rp = deckProfile(G, r);
    const rScore = scorePlayer(G, r).total + potential(G, r, T, rp);
    const rVp = scorePlayer(G, r).total;
    const threat = (rVp > scorePlayer(G, p).total ? 0.5 : 0.15) + (rScore > myScore ? 0.5 : 0.15);
    const rDelay = (r.deck.length + r.hand.length + r.discard.length) / rp.hand / 2;
    const gains = all.map((o) => (o.familiar ? -Infinity : gainFor(o.def, rp, T - 1, rDelay, rp.traitKey) * affordability(G, r, rp, o.c, o.def)));
    const sorted = [...gains].sort((a, b) => b - a);
    const top = sorted[0] ?? 0;
    const second = Math.max(0, sorted[1] ?? 0);
    gains.forEach((g, i) => {
      if (g === top && g > second) denial[i] = Math.max(denial[i] ?? 0, (g - second) * threat);
    });
  }
  const score = all.map((o, i) => (mine[i] ?? 0) + PARAMS.denialW * (denial[i] ?? 0) + o.price * 0.04 + (PARAMS.noiseW > 0 ? PARAMS.noiseW * 0.35 * jitter(G, o.action) : 0));
  // лучшая одиночная покупка и лучшая пара (две карты на ту же мощь)
  const turn = turnOf(G);
  let bestIdx = -1;
  let bestScore = PARAMS.buyMin;
  score.forEach((s, i) => {
    if (s > bestScore) {
      bestScore = s;
      bestIdx = i;
    }
  });
  let pairFirst = -1;
  let pairScore = bestScore;
  for (let i = 0; i < all.length; i++) {
    for (let j = i + 1; j < all.length; j++) {
      const a = all[i];
      const b = all[j];
      if (!a || !b) continue;
      if (a.c && b.c && a.c.iid === b.c.iid) continue;
      const needPower = a.price - a.chipPay + (b.price - b.chipPay);
      const needChips = a.chipPay + b.chipPay;
      if (needPower > turn.power || needChips > p.chips) continue;
      const s = (score[i] ?? 0) + (score[j] ?? 0);
      if ((score[i] ?? 0) > 0 && (score[j] ?? 0) > 0 && s > pairScore) {
        pairScore = s;
        pairFirst = (score[i] ?? 0) >= (score[j] ?? 0) ? i : j;
      }
    }
  }
  const idx = pairFirst >= 0 ? pairFirst : bestIdx;
  return idx >= 0 ? all[idx]?.action ?? null : null;
}

// ---------- решения в свой ход ----------

/**
 * Накладки уровней сложности поверх одного мозга: hard — полный мозг (честный, скрытое пересдаётся в fork),
 * cheat — тот же мозг, но видит скрытое; easy/normal — детерминированный шум оценок (розыгрыши, ответы, покупки):
 * бот ошибается там, где варианты близки, и не делает откровенных глупостей на очевидных развилках.
 * Выбор цели атаки остаётся принципиальным на всех уровнях (plainTarget/attackPool) — «гнобить непричастных»
 * нельзя даже по глупости. Величины шума калиброваны tools/level-audit.ts: доля совпадений с оптимальным
 * решением на спорных точках ~50% (normal) и ~30% (easy).
 */
export const LEVELS: Record<BotLevel, Partial<Params>> = {
  easy: { noiseW: 25 },
  normal: { noiseW: 6 },
  hard: {},
  cheat: {},
};

/** Подписи уровней для интерфейса (кнопки на старте хотсита, лобби, бейджи) */
export const LEVEL_INFO: Record<BotLevel, { emoji: string; label: string; hint: string }> = {
  easy: { emoji: '🐣', label: 'лёгкий', hint: 'играет заметно неоптимально' },
  normal: { emoji: '🤖', label: 'средний', hint: 'около половины решений — оптимальные' },
  hard: { emoji: '😎', label: 'сложный', hint: 'полная сила, честный: не подглядывает в руки и колоды' },
  cheat: { emoji: '😈', label: 'нечестный', hint: 'полная сила и видит чужие руки и колоды' },
};

/** Решение бота — ровно одно действие; уровень и характер игрока накладывают свои веса на время решения */
export function decide(G: Game, pid: PlayerId): Action | null {
  const p = player(G, pid);
  const level = p.botLevel ? LEVELS[p.botLevel] : null;
  const style = p.botStyle ? STYLES[p.botStyle].params : null;
  if ((!level || !Object.keys(level).length) && !style) return decideCore(G, pid);
  const overlay: Partial<Params> = { ...level, ...style };
  // шум уровня и шум «психа» складываются: псих на лёгком уровне ещё шумнее
  if (level?.noiseW != null && style?.noiseW != null) overlay.noiseW = level.noiseW + style.noiseW;
  const saved: Partial<Params> = {};
  for (const k of Object.keys(overlay) as (keyof Params)[]) saved[k] = PARAMS[k];
  Object.assign(PARAMS, overlay);
  try {
    return decideCore(G, pid);
  } finally {
    Object.assign(PARAMS, saved);
  }
}

function decideCore(G: Game, pid: PlayerId): Action | null {
  const p = player(G, pid);
  if (G.phase === 'setup') return decideSetup(G, p);
  if (G.phase !== 'play') return null;
  if (G.prompt) {
    if (promptWho(G) !== pid) return null;
    return { name: 'answer', args: [answerFor(G, pid, G.prompt)] };
  }
  const turn = turnOf(G);
  if (turn.player !== pid) return null;
  // 1. карты с руки: какую сыграть первой — по итогу хода (копия доигрывается дешёвой политикой)
  const gainOf = (c: CardInst): number | null => tryAction(G, pid, { name: 'playCard', args: [c.iid] }, 0, card(c).attack ? PARAMS.attackSamples : 1);
  const play = best(p.hand, gainOf);
  if (play) return { name: 'playCard', args: [play.item.iid] };
  // 2. активации карт и жетонов: обычные — если не хуже дешёвого доигрывания (оно их всё равно применит),
  //    ситуативные (уничтожить карту ради эффекта) — только если строго лучше
  const base = baseline(G, pid);
  const acts: { a: Action; situational: boolean }[] = [
    ...controlled(G, p).filter((c) => canActivate(G, c)).map((c) => ({ a: { name: 'activateCard', args: [c.iid] } as Action, situational: T.SITUATIONAL_ACTIVATE.has(c.cid) })),
    ...H.tokenActivations(G, p).filter((a) => a.enabled !== false).map((a) => ({ a: { name: 'activateCard', args: [a.id] } as Action, situational: false })),
  ];
  const act = best(acts, (x) => {
    const s = tryAction(G, pid, x.a);
    return s == null ? null : s - (x.situational ? 0.05 : -0.05);
  });
  if (act && act.score > base) return act.item.a;
  // 3. покупки (статическая модель — та же, что в доигрывании)
  const buy = bestBuy(G, p);
  if (buy) return buy;
  return { name: 'endTurn', args: [] };
}

/** Кому сейчас нужно действовать (для сервера): -1 — никому/игра окончена */
export function needPid(G: Game | null): PlayerId | -1 {
  if (!G) return -1;
  if (G.phase === 'setup') {
    const p = G.players.find((x) => x.bot && !setupDone(x));
    if (p) return p.id;
    const third = (G.setup?.third ?? []).find((id) => G.players[id]?.bot);
    return third ?? -1;
  }
  if (G.phase !== 'play' || !G.turn) return -1;
  return promptWho(G) ?? G.turn.player;
}

/** Чего ждут от игрока: подготовка (жетон и фамильяр), третий фамильяр, свой ход, решение в чужой ход */
export type WaitKind = 'setup' | 'third' | 'turn' | 'prompt';
export interface Waiting {
  pid: PlayerId;
  kind: WaitKind;
  /** ключ ожидания: сменился — это новое ожидание (уведомить снова), тот же — уже уведомлён */
  key: string;
}

/**
 * Кого ждёт партия — и людей, и ботов (игра по переписке будит людей уведомлениями). Подготовка идёт у всех сразу:
 * ждут все, кто не выбрал жетон и фамильяра; когда выбрали все, владельцы «трёх фамильяров» выбирают третьего из
 * ничейных (сброшенных) — тоже все сразу. В партии ждут одного: того, чьё решение нужно, иначе активного. Ключ хода —
 * номер хода: свои решения в свой ход — то же ожидание, что и сам ход.
 */
export function waitingFor(G: Game | null): Waiting[] {
  if (!G) return [];
  if (G.phase === 'setup') {
    const left = G.players.filter((x) => !setupDone(x));
    if (left.length) return left.map((x) => ({ pid: x.id, kind: 'setup', key: 'setup' }));
    return (G.setup?.third ?? []).map((pid) => ({ pid, kind: 'third', key: 'third' }));
  }
  if (G.phase !== 'play' || !G.turn) return [];
  const who = promptWho(G) ?? G.turn.player;
  const own = who === G.turn.player;
  return [{ pid: who, kind: own ? 'turn' : 'prompt', key: `${own ? 'turn' : 'prompt'}:${G.turnNo ?? 0}` }];
}

export type { CardInst };
